import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { BusinessObjectAssembler, Device, getNLengthHex } from '@iotv/datamodel';
import { getObjectHash } from '@iotv/iotv-v3-types';
import { Box, Button, Container, Grid, Tooltip, Typography } from '@mui/material';
import { useContext, useEffect, useMemo, useState } from 'react';
import { AppMainViewContext } from '../../ContextProviders/AppContextProvider';
import { hasHyperUser } from '../../helpers/access';
import { useThingUpdates } from '../../hooks/useThingUpdates';
import { SteppingLoadingModal } from '../../Popup/Loading';
import { ErrorState } from '../Invalid';
import { calculatePayloads } from './payloadParserCalculation';
import { PayloadParserContext } from './PayloadParserContext';
import PayloadParserData from './Steps/PayloadParserData';
import PayloadParserReadings from './Steps/PayloadParserReadings';
import PayloadParserTarget from './Steps/PayloadParserTarget';
const { REACT_APP_DEPLOYMENT_STAGE: env } = process.env;
const componentClassName = 'PayloadParserView';
const assembler = new BusinessObjectAssembler();
const deviceID = getNLengthHex(16);
/**
 * Emulation page to send downlinks to an entity. This allows us to:
 * 1) Test the decoding of downlink payloads
 * 2) Allow BA's to test use cases
 * 3) Allow us to test components
 */
const PayloadParserView = () => {
    const [{ userGroupRoles, contextUser, contextCustomer, contextProperty }] = useContext(AppMainViewContext);
    const hasHyperUserRole = hasHyperUser(userGroupRoles);
    const [entity, setEntity] = useState(undefined);
    const [usedEntityHash, setUsedEntityHash] = useState(undefined);
    const [entityType, setEntityType] = useState(undefined);
    const [selectedDeviceType, setSelectedDeviceType] = useState(undefined);
    const [readings, setReadings] = useState(undefined);
    const [size, setSize] = useState(null);
    const [reading, setReading] = useState(undefined);
    const device = useMemo(() => {
        const fakeDev = new Device({
            eui: deviceID,
            id: deviceID,
            pk: `Device:${deviceID}`,
            sk: `Device:${deviceID}`,
            deviceType: selectedDeviceType,
        });
        return fakeDev;
    }, [selectedDeviceType]);
    const entityCallback = (record) => {
        const { timestamp: updated_at, ...other } = record;
        let res = assembler.getInstance({ ...entity, ...other, updated_at });
        setEntity(res);
    };
    useThingUpdates({ contextUser, entity: entity, callback: entityCallback });
    const payloadButtonMessage = useMemo(() => {
        const message = [];
        if (readings === undefined || readings.length === 0) {
            message.push('No readings have been provided');
        }
        if (entity === undefined) {
            message.push('No entity has been selected');
        }
        if (message.length === 0) {
            return null;
        }
        return message.join(', ');
    }, [readings, entity]);
    const sendNextPayload = () => {
        // Check if there are readings to parse. When it has finished, reset
        if (readings === undefined || readings.length === 0) {
            setUsedEntityHash(undefined);
            setSize(null);
            setReadings(undefined);
            return;
        }
        if (size === null) {
            setSize(readings.length);
        }
        const updatedReadings = [...readings];
        const nextReading = updatedReadings.shift();
        setReadings(updatedReadings);
        setReading(nextReading);
    };
    // if the entity has changed, set the next `reading`
    useEffect(() => {
        const currentHash = getObjectHash(entity);
        if (usedEntityHash !== undefined && currentHash !== usedEntityHash) {
            sendNextPayload();
        }
    }, [entity, usedEntityHash]);
    // whenever next `reading` is set, send the payload
    useEffect(() => {
        if (reading) {
            setUsedEntityHash(getObjectHash(entity));
            entity && contextCustomer && calculatePayloads(entity, reading, device, contextCustomer, contextProperty);
            setReading(undefined);
        }
    }, [reading]);
    if (!hasHyperUserRole || env === 'prod') {
        return _jsx(ErrorState, { title: "Not Allowed", error: "Must be a Hyper User to use this feature!" });
    }
    return (_jsxs(PayloadParserContext.Provider, { value: { entity, entityType, readings, deviceType: selectedDeviceType }, children: [_jsxs(Container, { className: componentClassName, style: { maxWidth: 'unset' }, children: [_jsx(Typography, { variant: "h4", component: "h4", color: "primary", children: "Payload Parser" }), _jsxs(Grid, { container: true, spacing: 2, children: [_jsxs(Grid, { size: { xs: 12 }, children: [_jsx(PayloadParserData, { setReadings: setReadings }), readings && readings.length !== 0 && (_jsx(Box, { m: 1, children: _jsx(PayloadParserReadings, {}) }))] }), _jsxs(Grid, { size: { xs: 12 }, children: [_jsx(PayloadParserTarget, { setEntityType: setEntityType, setEntity: setEntity, setSelectedDeviceType: setSelectedDeviceType }), _jsx(Tooltip, { placement: "top", disableHoverListener: payloadButtonMessage === undefined, title: payloadButtonMessage, children: _jsx("span", { children: _jsx(Button, { fullWidth: true, disabled: payloadButtonMessage !== null, onClick: sendNextPayload, variant: "outlined", children: "Send payloads" }) }) })] })] })] }), size !== null && readings && readings.length > 0 && _jsx(SteppingLoadingModal, { current: size - readings.length, max: size })] }));
};
export default PayloadParserView;
